import { FaSolidCentSign, FaSolidX } from 'solid-icons/fa'
import { Component, createMemo, For, Show, useContext } from 'solid-js'
import type { Engine } from '../../lib/core/engine/Engine.type'
import type { PlayerGraphNode } from '../../lib/core/engine/PlayerGraphNode/PlayerGraphNode.type'
import createSampleEntity from '../../lib/core/entity/createSampleEntity'
import type { EntityTypeId } from '../../lib/core/entity/EntityTypeId.enum'
import entityTypeMetaList from '../../lib/core/entity/entityTypeMetaList.generated'
import hasSupplierInProximity from '../../lib/core/entity/hasSupplierInProximity'
import type { EntityTypeMeta } from '../../lib/core/EntityTypeMeta'
import findByIdOrThrow from '../../lib/core/findByIdOrThrow'
import formatGameMoney from '../../lib/core/player/formatGameMoney'
import { min } from '../../lib/core/util/math/Math'
import EngineContext from '../EngineContext'
import SingleEntityIcon3D from '../MapEditor/SingleEntityIcon3D'

interface Props {
  when: boolean
  setOpen: (newValue: boolean) => void
  player: PlayerGraphNode
  class: string
}

function increment<K>(map: Map<K, number>, key: K): void {
  map.set(key, (map.get(key) || 0) + 1)
}

function add<K>(map: Map<K, number>, key: K, addValue: number): void {
  map.set(key, (map.get(key) || 0) + addValue)
}

type EntityTypeFinancialSummary = {
  meta: EntityTypeMeta,
  count: number,
  income: number,
  repair: number
}

const PlayerMoneyCornerWidget: Component<Props> = (props) => {
  const engine: Engine = useContext(EngineContext)

  const entsSummary = createMemo<Array<EntityTypeFinancialSummary>>(() => {
    // const data = []
    const counts = new Map<EntityTypeId, number>()
    const nextRepairCost = new Map<EntityTypeId, number>()
    const incomes = new Map<EntityTypeId, number>()
    const { state } = engine
    const ents = props.player.ents()

    for (let index = 0; index < ents.length; index++) {
      const entity = ents[index]
      const entityMeta = findByIdOrThrow(entityTypeMetaList, entity.etype_id)

      const { price, entDefaults } = entityMeta
      const {
        hp: defaultHp,
        // fuel: defaultFuel,
        // mobility: defaultMobility,
      } = entDefaults as {
        fuel?: number
        hp?: number
        mobility?: number
      }

      increment(counts, entity.etype_id)

      if ('hp' in entity && price && defaultHp) {
        const prevHp = entity.hp || 0
        if (prevHp < defaultHp && hasSupplierInProximity(state, entity)) {
          const nextHp = min(defaultHp, prevHp + 2)
          const hpAdded = nextHp - prevHp
          const nextHpCreditCost = (hpAdded * price) / 10
          add(nextRepairCost, entity.etype_id, nextHpCreditCost)
        }
      }

      if (entityMeta.income) {
        add(incomes, entity.etype_id, entityMeta.income)
      }
    }
    return entityTypeMetaList.reduce((data: Array<EntityTypeFinancialSummary>, entityTypeMeta: EntityTypeMeta) => {
      if (counts.has(entityTypeMeta.id)) {
        data.push({
          meta: entityTypeMeta,
          count: counts.get(entityTypeMeta.id) || 0,
          income: incomes.get(entityTypeMeta.id) || 0,
          repair: nextRepairCost.get(entityTypeMeta.id) || 0,
        })
      }
      return data
    }, [])
  })

  const netIncome = createMemo<number>(() => {
    return entsSummary().reduce((sum, row) => sum + row.income - row.repair, 0)
  })

  return (
    <Show when={props.when}>
      <div class="clear-both" />
      <div class={`cw cw-player-money ${props.class}`}>
        <table>
          <thead>
            <tr>
              <th />
              <th class="text-end">Count</th>
              <th class="text-end">Income</th>
              <th class="text-end">Repairs</th>
            </tr>
          </thead>
          <tbody>
            <For each={entsSummary()} children={(entSummary) => {
              return (<tr>
                <td title={entSummary.meta.dname}>
                  <SingleEntityIcon3D
                    entity={createSampleEntity(entSummary.meta.id, props.player.id)}
                    size={24}
                  />
                </td>
                <td class="text-end text-monospace">{entSummary.count}</td>
                <td class="text-end">
                  <Show when={entSummary.income !== 0}>
                    <span class="text-muted ps-1"><FaSolidCentSign /></span>{formatGameMoney(entSummary.income)}
                  </Show>
                </td>
                <td class="text-end">
                  <Show when={entSummary.repair !== 0}>
                    (<span class="text-muted ps-1"><FaSolidCentSign /></span>{formatGameMoney(entSummary.repair)})
                  </Show>
                </td>
              </tr>)
            }} />
          </tbody>
        </table>
        <div class="float-start">
          <span class="text-muted">Net Income:</span>
          <span class="text-muted ps-1"><FaSolidCentSign /></span>{formatGameMoney(netIncome())}
        </div>
        <button class="btn btn-secondary btn-sm float-end" onClick={() => {
          props.setOpen(false)
        }}><FaSolidX /> Close</button>
      </div>
    </Show>
  )
}

export default PlayerMoneyCornerWidget
